import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiSearch, FiX, FiCheck, FiUser } from "react-icons/fi";
import request from "../../utils/request";

/**
 * HostSearchPicker — a 2026-standard searchable host combobox.
 *
 * Design language (matches CommandPalette + Header patterns already in the
 * admin app):
 *   - `rounded-xl` control + `rounded-2xl shadow-2xl` floating card
 *   - indigo hover / selected accents on zinc grays
 *   - keyboard first: ArrowUp / ArrowDown / Home / End / Enter / Escape
 *   - async-friendly: loads the full host list once, then filters in-memory
 *     (the backend `/hosts` admin endpoint returns the full roster in a
 *     single round-trip, so an extra debounced server round-trip would just
 *     add latency without improving accuracy)
 *   - clear-on-click, "Selected host" chip when a value is set
 *   - a11y: `role="combobox"`, `aria-expanded`, `aria-activedescendant`,
 *     listbox `role="listbox"`, options `role="option"`
 *
 * Props:
 *   value        — selected host id (string)
 *   onChange     — (hostId, hostObject|null) => void
 *   placeholder  — optional; defaults to 'Search hosts by name or email…'
 *   disabled     — bool
 *   error        — optional error text to render beneath
 *   autoFocus    — bool
 */
export default function HostSearchPicker({
  value,
  onChange,
  placeholder = "Search hosts by name or email…",
  disabled = false,
  error,
  autoFocus = false,
}) {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // One-shot fetch. The admin host roster is small enough (hundreds, not
  // tens of thousands) that client-side filtering is faster than a
  // debounced server call and also feels instant. If the roster ever grows
  // past a few thousand, switch this to debounced `?search=` on the server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await request.get("/hosts");
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : res.data?.hosts || [];
        const normalized = raw
          .map((h) => ({
            id: h._id || h.id,
            firstName: h.firstName || "",
            lastName: h.lastName || "",
            email: h.email || "",
            isVerified: !!h.isVerified,
            isOnBoarded: !!h.isOnBoarded,
            phoneNumber: h.phoneNumber || "",
          }))
          .filter((h) => h.id)
          .sort((a, b) =>
            `${a.firstName} ${a.lastName}`.localeCompare(
              `${b.firstName} ${b.lastName}`
            )
          );
        setHosts(normalized);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load hosts."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = useMemo(
    () => (value ? hosts.find((h) => h.id === value) : null),
    [value, hosts]
  );

  // Ranked filter: name match > email match, prefix > contains. Keeps the
  // dropdown useful even when typing a surname mid-email.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return hosts.slice(0, 50);
    const scored = [];
    for (const h of hosts) {
      const name = `${h.firstName} ${h.lastName}`.toLowerCase();
      const email = (h.email || "").toLowerCase();
      let score = 0;
      if (name.startsWith(q)) score += 6;
      else if (name.includes(` ${q}`)) score += 4;
      else if (name.includes(q)) score += 3;
      if (email.startsWith(q)) score += 4;
      else if (email.includes(q)) score += 2;
      if (score > 0) scored.push({ h, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 50).map((s) => s.h);
  }, [query, hosts]);

  // Keep cursor in range when list length changes.
  useEffect(() => {
    if (cursor >= filtered.length) setCursor(0);
  }, [filtered.length, cursor]);

  const commit = (host) => {
    if (!host) return;
    onChange?.(host.id, host);
    setOpen(false);
    setQuery("");
  };

  const clear = () => {
    onChange?.("", null);
    setQuery("");
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setCursor((c) => Math.min(filtered.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setCursor(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setCursor(filtered.length - 1);
    } else if (e.key === "Enter") {
      if (!open) {
        setOpen(true);
        return;
      }
      e.preventDefault();
      commit(filtered[cursor]);
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  // Scroll active option into view on cursor change.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-option-index="${cursor}"]`
    );
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [cursor, open, filtered.length]);

  const initials = (h) =>
    `${(h.firstName || "?")[0] || ""}${(h.lastName || "")[0] || ""}`.toUpperCase();

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`relative flex items-center gap-2 rounded-xl border bg-white px-3 py-2 shadow-sm transition-all ${
          disabled
            ? "cursor-not-allowed border-zinc-200 bg-zinc-50 text-zinc-400"
            : open
              ? "border-indigo-500 ring-2 ring-indigo-500/20"
              : error
                ? "border-red-300 hover:border-red-400"
                : "border-zinc-300 hover:border-zinc-400"
        }`}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-owns="host-picker-listbox"
        onClick={() => {
          if (disabled) return;
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        <FiSearch className="h-4 w-4 flex-none text-zinc-400" />

        {selected && !open ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-700">
              {initials(selected) || <FiUser className="h-3 w-3" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-zinc-900">
                {selected.firstName} {selected.lastName}
              </div>
              <div className="truncate text-xs text-zinc-500">
                {selected.email || "—"}
              </div>
            </div>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            disabled={disabled}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
              setCursor(0);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={
              loading
                ? "Loading hosts…"
                : selected
                  ? "Search to change…"
                  : placeholder
            }
            className="flex-1 bg-transparent text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none"
            aria-controls="host-picker-listbox"
            aria-activedescendant={
              open && filtered[cursor]
                ? `host-opt-${filtered[cursor].id}`
                : undefined
            }
          />
        )}

        {selected ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            className="flex-none rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="Clear selection"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <div
          className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl backdrop-blur"
          role="presentation"
        >
          <div className="flex items-center gap-2 border-b border-zinc-100 bg-zinc-50/60 px-3 py-2">
            <FiSearch className="h-3.5 w-3.5 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCursor(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Type a name or email…"
              autoFocus
              className="flex-1 bg-transparent text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none"
            />
            <span className="text-[10px] font-medium text-zinc-400">
              {filtered.length} of {hosts.length}
            </span>
          </div>

          <ul
            ref={listRef}
            id="host-picker-listbox"
            role="listbox"
            className="max-h-72 overflow-y-auto py-1"
          >
            {loading ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">
                Loading hosts…
              </li>
            ) : loadError ? (
              <li className="px-3 py-4 text-xs text-red-600">{loadError}</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-zinc-500">
                No hosts match{query ? ` "${query}"` : ""}.
              </li>
            ) : (
              filtered.map((h, idx) => {
                const active = idx === cursor;
                const isSelected = value === h.id;
                return (
                  <li
                    key={h.id}
                    id={`host-opt-${h.id}`}
                    data-option-index={idx}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setCursor(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(h);
                    }}
                    className={`mx-1 flex cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2 transition-colors ${
                      active ? "bg-indigo-50" : "hover:bg-zinc-50"
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11px] font-semibold ${
                        active
                          ? "bg-indigo-600 text-white"
                          : "bg-zinc-100 text-zinc-700"
                      }`}
                    >
                      {initials(h) || <FiUser className="h-3.5 w-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-zinc-900">
                          {h.firstName} {h.lastName}
                        </span>
                        {h.isVerified && h.isOnBoarded ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">
                            Pending
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-zinc-500">
                        {h.email || "—"}
                        {h.phoneNumber ? ` · ${h.phoneNumber}` : ""}
                      </div>
                    </div>
                    {isSelected ? (
                      <FiCheck className="h-4 w-4 text-indigo-600" />
                    ) : (
                      <span className="text-[10px] font-mono text-zinc-400">
                        {String(h.id).slice(-6)}
                      </span>
                    )}
                  </li>
                );
              })
            )}
          </ul>

          <div className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/60 px-3 py-1.5 text-[10px] text-zinc-500">
            <span>
              <kbd className="rounded border border-zinc-200 bg-white px-1 font-sans text-[10px]">
                ↑↓
              </kbd>{" "}
              navigate{" "}
              <kbd className="rounded border border-zinc-200 bg-white px-1 font-sans text-[10px]">
                ↵
              </kbd>{" "}
              select{" "}
              <kbd className="rounded border border-zinc-200 bg-white px-1 font-sans text-[10px]">
                esc
              </kbd>{" "}
              close
            </span>
            {selected ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
                className="rounded-md px-2 py-0.5 text-zinc-600 hover:bg-zinc-100"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : null}
    </div>
  );
}
